// 設計基準解析度 — 取自 Unity ProjectSettings／prefab（橫版 1920×1080 已由 ProjectSettings.asset 驗證；
// 直版 1080×1920 已由 PoriraitUI.prefab 的 CanvasScaler 覆寫驗證（ScaleWithScreenSize + Expand））。
// Web canvas 預設 960×600（同樣取自 ProjectSettings.asset）。
// 供資源製作與佈局比例參考；實際渲染採等比縮放至視窗。

export type Orientation = 'landscape' | 'portrait'

export interface DesignSize {
  readonly width: number
  readonly height: number
}

export const DESIGN: Readonly<Record<Orientation, DesignSize>> = {
  landscape: { width: 1920, height: 1080 },
  portrait: { width: 1080, height: 1920 },
}

export function orientationOf(width: number, height: number): Orientation {
  return width >= height ? 'landscape' : 'portrait'
}

/**
 * 佈局度量（佔螢幕高度比例）— 橫/直版的單一事實來源。
 * 底部操作列高度、頂部保留，由各場景共用以保持對位一致。
 * 數值為初始佔位估計，待實際 UI 版面（底部操作列／HUD）確定後再校正。
 */
export interface LayoutMetrics {
  readonly barHeight: number
  readonly topReserve: number
}

export function layoutMetrics(orientation: Orientation): LayoutMetrics {
  return orientation === 'portrait'
    ? { barHeight: 0.17, topReserve: 0.09 }
    : { barHeight: 0.15, topReserve: 0.05 }
}

/** 可隨視窗/方向重新佈局的場景元件。 */
export interface Resizable {
  resize(width: number, height: number, orientation: Orientation): void
}

/**
 * 橫版 Unity Canvas 等比縮放係數（對應 Unity 全螢幕 Canvas reference 1920×1080、Expand 模式）。
 * 供各 HUD 以 Unity prefab 的 anchoredPosition/sizeDelta 原值排版（見 unityAnchorLandscape）。
 */
export function unityScaleLandscape(width: number, height: number): number {
  return Math.min(width / DESIGN.landscape.width, height / DESIGN.landscape.height)
}

/**
 * Unity anchoredPosition（1920×1080、畫面中心原點、y 向上）→ 螢幕座標（橫版）。
 * 以畫面中心對齊、unityScaleLandscape 等比縮放，即 Unity 全螢幕 Canvas 的顯示行為。
 */
export function unityAnchorLandscape(
  ax: number,
  ay: number,
  width: number,
  height: number,
): { readonly x: number; readonly y: number } {
  const s = unityScaleLandscape(width, height)
  return { x: width / 2 + ax * s, y: height / 2 - ay * s }
}

/**
 * 直版 Unity Canvas 等比縮放係數（PoriraitUI.prefab CanvasScaler：reference 1080×1920、Expand 模式）。
 * 供各 HUD 以 PoriraitUI.prefab（LandscapeUI 的 Prefab Variant）覆寫座標原值排版。
 */
export function unityScalePortrait(width: number, height: number): number {
  return Math.min(width / DESIGN.portrait.width, height / DESIGN.portrait.height)
}

/** Unity anchoredPosition（1080×1920、畫面中心原點、y 向上）→ 螢幕座標（直版）。 */
export function unityAnchorPortrait(
  ax: number,
  ay: number,
  width: number,
  height: number,
): { readonly x: number; readonly y: number } {
  const s = unityScalePortrait(width, height)
  return { x: width / 2 + ax * s, y: height / 2 - ay * s }
}
